import type { SchoolType } from '@mathflat/domain/SchoolSystem'
import type { SchoolTestType } from '@mathflat/domain/SchoolTest'
import { makeAutoObservable } from 'mobx'

import commonRepo from '@/repository/common.repo'
import { curriculumCoreService, type ExpendCoreItem } from '@/service/curriculumCore.service'
import { localStorageService } from '@/service/storage.service'

import { DropdownNode } from '../models/DropdownNode.model'
import { DropdownStore } from '../stores/Dropdown.store'
import type { SchoolTestDropdownType } from '../types/dropdown.type'

const BY_SCHOOL_TEST_OPTION = 'BY_SCHOOL_TEST_OPTION'

interface Props {
  middleSchoolGradeSemesterList: ExpendCoreItem[]
  highSchoolSubjectList: ExpendCoreItem[]
}

export class SchoolTestDropdownManager {
  static DEFAULT_YEARS = [2021, 2022, 2023, 2024, 2025, 2026]

  private _yearFilterOptions = new DropdownStore()
  private _gradeSemesterFilterOptions = new DropdownStore<DropdownNode>()
  private _highSchoolSubjectFilterOptions = new DropdownStore<DropdownNode>()
  private _testTypeFilterOption = new DropdownStore<DropdownNode<SchoolTestType | 'ALL'>>()
  private _schoolTypeOptions = new DropdownStore<DropdownNode<Exclude<SchoolType, 'ELEMENTARY'>>>()

  get yearFilterOptions() {
    return this._yearFilterOptions
  }

  get gradeSemesterFilterOptions() {
    return this._gradeSemesterFilterOptions
  }

  get testTypeFilterOption() {
    return this._testTypeFilterOption
  }

  get highSchoolSubjectFilterOptions() {
    return this._highSchoolSubjectFilterOptions
  }

  get schoolTypeOptions() {
    return this._schoolTypeOptions
  }

  get currentChecked() {
    return {
      years: this._yearFilterOptions.dropdownList.find((node) => node.checked),
      testType: this._testTypeFilterOption.dropdownList.find((node) => node.checked),
      gradeSemester: this._gradeSemesterFilterOptions.dropdownList.find((node) => node.checked),
      highSchoolSubject: this._highSchoolSubjectFilterOptions.dropdownList.find(
        (node) => node.checked,
      ),
      schoolType: this._schoolTypeOptions.dropdownList.find((node) => node.checked),
    }
  }

  constructor(
    settings: Props = {
      middleSchoolGradeSemesterList: curriculumCoreService.schoolTestCurriculum.middle,
      highSchoolSubjectList: curriculumCoreService.schoolTestCurriculum.high,
    },
  ) {
    makeAutoObservable(this)
    this.initDropdownSettings(settings)
  }

  private initDropdownSettings(settings: Props) {
    const deviceBasedDropdownOptions = localStorageService.getItem(BY_SCHOOL_TEST_OPTION)

    const setCurriculumDropdownNode = (
      curriculum: ExpendCoreItem,
      isSameAsDeviceStorage: boolean,
    ) => {
      const disabled = curriculum.status === 'NOT_READY'

      const checked = (() => {
        if (commonRepo.isSolo) {
          return curriculum.subscribeStatus === 'SUBSCRIBED' && !disabled
        }

        return (
          (isSameAsDeviceStorage && curriculum.subscribeStatus === 'SUBSCRIBED' && !disabled) ||
          (curriculum.subscribeStatus === 'SUBSCRIBED' && !disabled)
        )
      })()

      return new DropdownNode(
        curriculum.trieKey,
        curriculum.labelShort,
        checked,
        disabled,
        curriculum.subscribeStatus,
      )
    }

    const middleCurriculums = settings.middleSchoolGradeSemesterList.map((curriculum) => {
      const isSameAsDeviceStorage = deviceBasedDropdownOptions?.curriculumKey === curriculum.trieKey
      return setCurriculumDropdownNode(curriculum, isSameAsDeviceStorage)
    })
    this._gradeSemesterFilterOptions.setDropdownList(middleCurriculums)

    const highCurriculums = settings.highSchoolSubjectList.map((curriculum) => {
      const isSameAsDeviceStorage =
        deviceBasedDropdownOptions?.highSchoolCurriculumKey === curriculum.trieKey
      return setCurriculumDropdownNode(curriculum, isSameAsDeviceStorage)
    })
    this._highSchoolSubjectFilterOptions.setDropdownList(highCurriculums)

    const schoolTestSchools = curriculumCoreService.schoolTestCurriculum.schools.map((school) => {
      const checked = (() => {
        if (commonRepo.isSolo) {
          return school.subscribeStatus === 'SUBSCRIBED'
        }
        return (
          deviceBasedDropdownOptions?.schoolType === school.value ||
          school.subscribeStatus === 'SUBSCRIBED'
        )
      })()
      return new DropdownNode(
        school.value,
        school.labelFull,
        checked,
        false,
        school.subscribeStatus,
      )
    })

    this._schoolTypeOptions.setDropdownList(
      schoolTestSchools as DropdownNode<Exclude<SchoolType, 'ELEMENTARY'>>[],
    )

    if (!this.currentChecked.schoolType?.id) {
      this._schoolTypeOptions.updateCheckedNodes('MIDDLE')
    }

    this._testTypeFilterOption.setDropdownList([
      new DropdownNode('ALL', '중간기말 전체', true),
      new DropdownNode('MIDTERM', '중간고사'),
      new DropdownNode('FINAL', '기말고사'),
    ])

    this._yearFilterOptions.setDropdownList([
      new DropdownNode('ALL', '연도 전체'),
      new DropdownNode('2023', '2023년', false),
    ])
  }

  updateFilterOptions({
    year,
    gradeSemester,
    highSchoolSubject,
    schoolType,
    testType,
  }: SchoolTestDropdownType) {
    year && this._yearFilterOptions.updateCheckedNodes(year)
    gradeSemester && this._gradeSemesterFilterOptions.updateCheckedNodes(gradeSemester)
    highSchoolSubject && this._highSchoolSubjectFilterOptions.updateCheckedNodes(highSchoolSubject)
    schoolType && this._schoolTypeOptions.updateCheckedNodes(schoolType)
    testType && this._testTypeFilterOption.updateCheckedNodes(testType)
  }
}
